import { useEffect, useState } from "react";
import { DataPaginate } from "../../../../../models/utils/DataPaginate";
import productsService, { ProductFilters } from "../../../../../services/products.service";
import useProductTableColumn from "./use-product-column";
import Table from "../../../../../components/fundamentals/table";
import { Product } from "../../../../../models/Product";
import ListIcon from "../../../../../components/fundamentals/icons/list-icon";
import TileIcon from "../../../../../components/fundamentals/icons/tile-icon";
import clsx from "clsx";
import ProductList from "../product-list/ProductList";
import TableContainer from "../../../../../components/fundamentals/table-container";
import {
  EFilter,
  IFilter,
} from "../../../../../models/utils/filter/FilterType.interface";
import ProductRow from "./ProductRow";
import {getTablePagingState} from "../../../../../services/utils/TableHelper";
import Checkbox from "../../../../../components/fundamentals/checkbox";
import BulkActionBar from "./BulkActionBar";

export enum columnProduct {
  SELECT = "select",
  NAME = "Nom du produit",
  PROVIDER = "Fournisseur",
  BRAND = "Marque",
  SOURCE = "Source",
  PRICE = "Prix à l'unité",
  EAN = "EAN13",
  STOCK = "Stock global",
  VISIBILITY = "Visibilité",
  ACTION = `action`,
  REMOVE = '',
  PIM = 'Synchro PIM'
}

interface IProps {
  columns?: columnProduct[],
  selected?: Product[],
  setSelected?: (products: Product[]) => void
  displaySelected?: boolean,
  className?: string,
  useElasticSearch?: boolean,
  // RAG-71 : active la sélection multiple + la barre d'actions en masse (catégorie / visibilité).
  enableBulkActions?: boolean
}
export default function ProductTable( 
  {
    columns = [
                columnProduct.NAME,
                columnProduct.PROVIDER,
                columnProduct.BRAND,
                columnProduct.SOURCE,
                columnProduct.PIM,
                columnProduct.PRICE,
                columnProduct.EAN,
                columnProduct.STOCK,
                columnProduct.VISIBILITY,
                columnProduct.ACTION
              ],
    selected,
    setSelected,
    displaySelected = false,
    className,
    useElasticSearch = false,
    enableBulkActions = false
  }: IProps) {
  // RAG-71 : en mode « actions en masse », la table gère sa propre sélection (la prop
  // contrôlée `selected` sert au sélecteur de produits des promotions, cas distinct).
  const [bulkSelected, setBulkSelected] = useState<Product[]>([]);
  const effectiveSelected = enableBulkActions ? bulkSelected : selected;
  const effectiveSetSelected = enableBulkActions ? setBulkSelected : setSelected;
  const effectiveColumns = enableBulkActions && !columns.includes(columnProduct.SELECT)
    ? [columnProduct.SELECT, ...columns]
    : columns;
  const [dataPaginate, setDataPaginate] = useState<DataPaginate>();
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState<string>("");
  const [tmpSearch, setTmpSearch] = useState<string>("");
  const [showList, setShowList] = useState(true);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState<IFilter[]>();
  // RAG-38 : filtres fournisseur / PIM source
  const [productFilters, setProductFilters] = useState<ProductFilters>({});
  const [tmpProvider, setTmpProvider] = useState<string>("");

  const setTileView = () => {
    setLimit(10);
    setShowList(false);
  };
  const setListView = () => {
    setLimit(10);
    setShowList(true);
  };
  // const [columns] = useProductTableColumn({
  //   setTileView,
  //   setListView,
  //   showList,
  // });
    async function fetchProducts() {
        setIsLoading(true);

        let dataPaginate: DataPaginate;

        if (useElasticSearch) {
            dataPaginate = await productsService.listWithElastic(search, currentPage);
        } else {
            dataPaginate = await productsService.list(currentPage, search, filters, null, productFilters);
        }

        setDataPaginate(dataPaginate);

        setIsLoading(false);
    }

  useEffect(() => {
    !displaySelected && fetchProducts();
  }, [currentPage, search, filters, productFilters]);

  // RAG-38 : filtre fournisseur (débounce, comme la recherche).
  useEffect(() => {
    const delay = setTimeout(() => {
      setCurrentPage(1);
      setProductFilters((f) => ({ ...f, provider: tmpProvider || undefined }));
    }, 400);
    return () => clearTimeout(delay);
  }, [tmpProvider]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setCurrentPage(1);
      setSearch(tmpSearch);
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [tmpSearch]);
  // ---- FILTERS ----
  // useEffect(() => {
  //   setFilters([
  //     {
  //       name: "status",
  //       type: EFilter.SELECT_MULTIPLE,
  //       label: "Status",
  //       list: [
  //         {
  //           label: "proposed",
  //           value: "proposed",
  //         },
  //         {
  //           label: "draft",
  //           value: "draft",
  //         },
  //         {
  //           label: "published",
  //           value: "published",
  //         },
  //         {
  //           label: "rejected",
  //           value: "rejected",
  //         },
  //       ],
  //     },
  //     {
  //       name: "category_id",
  //       type: EFilter.SELECT_MULTIPLE,
  //       label: "Category",
  //       list: [
  //         {
  //           label: "Category1",
  //           value: 1,
  //         },
  //       ],
  //     },
  //     {
  //       name: "tags",
  //       type: EFilter.TAG,
  //       label: "Tags",
  //     },
  //   ]);
  // }, []);

  // RAG-71 : « tout cocher » sur la page courante (la sélection persiste entre les pages).
  const currentPageProducts: Product[] = dataPaginate?.data ?? [];
  const pageAllSelected =
    currentPageProducts.length > 0 &&
    currentPageProducts.every((p) => bulkSelected.some((s) => s.id === p.id));
  const toggleSelectPage = () => {
    if (pageAllSelected) {
      const ids = new Set(currentPageProducts.map((p) => p.id));
      setBulkSelected(bulkSelected.filter((s) => !ids.has(s.id)));
    } else {
      const existing = new Set(bulkSelected.map((s) => s.id));
      setBulkSelected([...bulkSelected, ...currentPageProducts.filter((p) => !existing.has(p.id))]);
    }
  };

  return (
    <div
      className={clsx(
        className
      )}
    >
      {enableBulkActions && effectiveSelected && effectiveSelected.length > 0 && (
        <BulkActionBar
          count={effectiveSelected.length}
          productIds={effectiveSelected.map((p) => p.id)}
          onClear={() => setBulkSelected([])}
          onDone={() => {
            setBulkSelected([]);
            return fetchProducts();
          }}
        />
      )}
      {!displaySelected && (
        <div className="flex flex-wrap items-end gap-3 mb-base">
          <div className="flex flex-col">
            <label className="inter-small-semibold mb-1">Fournisseur</label>
            <input
              className="bg-grey-5 border border-gray-20 rounded-rounded h-10 px-small outline-none focus:border-blue-60"
              placeholder="Code (ex : 3M)"
              value={tmpProvider}
              onChange={(e) => setTmpProvider(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="inter-small-semibold mb-1">PIM source</label>
            <select
              className="bg-grey-5 border border-gray-20 rounded-rounded h-10 px-small outline-none focus:border-blue-60"
              value={productFilters.source ?? "all"}
              onChange={(e) => {
                const value = e.target.value;
                setCurrentPage(1);
                setProductFilters((f) => ({ ...f, source: value === "all" ? undefined : value }));
              }}
            >
              <option value="all">Toutes</option>
              <option value="none">Socoda</option>
              <option value="interactiv">Interactiv</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="inter-small-semibold mb-1">Plateforme Socoda</label>
            <select
              className="bg-grey-5 border border-gray-20 rounded-rounded h-10 px-small outline-none focus:border-blue-60"
              value={productFilters.is_platform ?? "all"}
              onChange={(e) => {
                const value = e.target.value;
                setCurrentPage(1);
                setProductFilters((f) => ({ ...f, is_platform: value === "all" ? undefined : value }));
              }}
            >
              <option value="all">Tous les produits</option>
              <option value="1">Plateforme uniquement</option>
            </select>
          </div>
        </div>
      )}
      <TableContainer
        numberOfRows={displaySelected ? selected?.length : 10}
        hasPagination={!displaySelected}
        pagingState={getTablePagingState(dataPaginate!, setCurrentPage, 'résultats')}
        isLoading={isLoading}
      >
        <Table
          enableSearch={displaySelected ? false : true}
          searchValue={tmpSearch}
          handleSearch={(term) => setTmpSearch(term)}
          // filters={displaySelected ? undefined : filters}
          // onClear={() => {
          //   setCurrentPage(1);
          //   if (filters) {
          //     setFilters(
          //       filters.map((f) => {
          //         f.value = undefined;
          //         return f;
          //       })
          //     );
          //   }
          // }}
          // onFilter={(filters: IFilter[]) => {
          //   setCurrentPage(1);
          //   setFilters(filters);
          // }}
        >
          {showList ? (
            <>
              <Table.Head>
                <Table.HeadRow>
                  {effectiveColumns.map((col: columnProduct, index) => {
                    if (col === columnProduct.ACTION) {
                      return (
                        <Table.HeadCell key={`product-head-cell-action-${index}`}>
                          <div className="text-right flex justify-end">
                            <span
                              onClick={setListView}
                              className={clsx(
                                "hover:bg-grey-5 cursor-pointer rounded p-0.5",
                                {
                                  "text-grey-90": showList,
                                  "text-grey-40": !showList,
                                }
                              )}
                            >
                              <ListIcon size={20} />
                            </span>
                            <span
                              onClick={setTileView}
                              className={clsx(
                                "hover:bg-grey-5 cursor-pointer rounded p-0.5",
                                {
                                  "text-grey-90": !showList,
                                  "text-grey-40": showList,
                                }
                              )}
                            >
                              <TileIcon size={20} />
                            </span>
                          </div>
                        </Table.HeadCell>
                      )
                    } else if (col === columnProduct.SELECT) {
                      return (
                        <Table.HeadCell key={`product-head-cell-select-${index}`}>
                          {enableBulkActions && !displaySelected && (
                            <Checkbox label={undefined} checked={pageAllSelected} onChange={() => toggleSelectPage()} />
                          )}
                        </Table.HeadCell>
                      )
                    } else return (
                      <Table.HeadCell key={`product-head-cell-other-${index}`}>{col}</Table.HeadCell>
                    )
                  })}
                </Table.HeadRow>
              </Table.Head>
              <Table.Body>
                {showList && !displaySelected &&
                  dataPaginate?.data.map((product: Product) => (
                    <ProductRow
                      selected={effectiveSelected}
                      setSelected={effectiveSetSelected}
                      key={`product_item_${product.id}`}
                      product={product}
                      columns={effectiveColumns}
                      refreshProductsData={fetchProducts}
                    />
                  ))}
                {displaySelected && selected &&
                  selected.map((product: Product) => (
                    <ProductRow
                      selected={selected}
                      setSelected={setSelected}
                      key={`product_item_only_display_${product.id}`}
                      product={product}
                      columns={effectiveColumns}
                      refreshProductsData={fetchProducts}
                    />
                  ))
                }
              </Table.Body>
            </>
          ) : (
            <ProductList
              products={dataPaginate ? dataPaginate.data : []}
              toggleListView={setListView}
            />
          )}
        </Table>
      </TableContainer>
    </div>
  );
}
